import React, {useMemo} from "react";
import {CHECKBOX_PARTIAL} from "@renta-apps/renta-react-components";
import FuelAlertSubscriptionManagement from "@/components/AlertSubscriptionsManagement/FuelAlertSubscriptionManagement";
import BatteryAlertSubscriptionManagement from "@/components/AlertSubscriptionsManagement/BatteryAlertSubscriptionManagement";
import TheftAlertSubscriptionManagement from "@/components/AlertSubscriptionsManagement/TheftAlertSubscriptionManagement";
import IdleAlertSubscriptionManagement from "@/components/AlertSubscriptionsManagement/IdleAlertSubscriptionManagement";
import {SubscriptionAlertType} from "@/models/Enums";
import AlarmData from "@/models/server/AlarmData";
import styles from "./AlertSubscriptionsManagement.module.scss";
import EnvironmentControlAirTemperatureAlertSubscriptionManagement from "@/components/AlertSubscriptionsManagement/EnvironmentControlAirTemperatureAlertSubscriptionManagement";
import EnvironmentControlStructureTemperatureAlertSubscriptionManagement from "@/components/AlertSubscriptionsManagement/EnvironmentControlStructureTemperatureAlertSubscriptionManagement";
import EnvironmentControlStructureHumidityAlertSubscriptionManagement from "@/components/AlertSubscriptionsManagement/EnvironmentControlStructureHumidityAlertSubscriptionManagement";
import EnvironmentControlAirHumidityAlertSubscriptionManagement from "@/components/AlertSubscriptionsManagement/EnvironmentControlAirHumidityAlertSubscriptionManagement";

export type AlertState = boolean | typeof CHECKBOX_PARTIAL;

export type SubscriptionAlertConfig = {
    type: SubscriptionAlertType;
    supported: boolean;
    enabled: AlertState;
    numberOfDevices?: number;
    isAdjustmentEnabled?: boolean;
    alarmData?: AlarmData;
};

interface IAlertSubscriptionsManagementProps {
    itemKey?: string;
    disabled: boolean;
    singleDevice: boolean;
    numberOfDevices?: number;
    alerts: SubscriptionAlertConfig[];
    onChange: (type: SubscriptionAlertType, value: AlertState) => void;
    onAlertDataChange: (type: SubscriptionAlertType, value: AlarmData) => void;
    onAdjustmentChange: (type: SubscriptionAlertType, isAdjustmentEnabled: boolean) => void;
}

const AlertTypeToComponentMap = {
    [SubscriptionAlertType.Fuel]: FuelAlertSubscriptionManagement,
    [SubscriptionAlertType.Battery]: BatteryAlertSubscriptionManagement,
    [SubscriptionAlertType.Theft]: TheftAlertSubscriptionManagement,
    [SubscriptionAlertType.Idle]: IdleAlertSubscriptionManagement,
    [SubscriptionAlertType.EnvironmentControlStructureTemperature]: EnvironmentControlStructureTemperatureAlertSubscriptionManagement,
    [SubscriptionAlertType.EnvironmentControlAirTemperature]: EnvironmentControlAirTemperatureAlertSubscriptionManagement,
    [SubscriptionAlertType.EnvironmentControlStructureHumidity]: EnvironmentControlStructureHumidityAlertSubscriptionManagement,
    [SubscriptionAlertType.EnvironmentControlAirHumidity]: EnvironmentControlAirHumidityAlertSubscriptionManagement,
};

const AlertSubscriptionsManagement: React.FC<IAlertSubscriptionsManagementProps> = (props: IAlertSubscriptionsManagementProps) => {
    const components = useMemo(() => {
        return props.alerts.filter(alert => !props.singleDevice || alert.supported).map((alert, index) => {
            const Component = AlertTypeToComponentMap[alert.type];
            return (
                <Component disabled={props.disabled}
                           key={props.itemKey ? `${props.itemKey}_${alert.type}` : alert.type}
                           itemKey={props.itemKey}
                           checked={alert.enabled}
                           singleDevice={props.singleDevice}
                           numberOfDevices={props.numberOfDevices}
                           numberOfSupportedDevices={alert.numberOfDevices}
                           rowIndex={index}
                           onChange={(value: boolean) => props.onChange(alert.type, value)}
                           onAlertDataChange={(value: AlarmData) => props.onAlertDataChange(alert.type, value)}
                           value={alert.alarmData?.value}
                           min={alert.alarmData?.min}
                           max={alert.alarmData?.max}
                           onAdjustmentChange={(value: boolean) => props.onAdjustmentChange(alert.type, value)}
                           isAdjustmentEnabled={alert.isAdjustmentEnabled}
                />
            );
        });
    }, [props.disabled, props.alerts]);

    return (
        <div className={styles.dataTable}>
            <div className={styles.dataRows}>
                {components}
            </div>
        </div>
    );
};

export default AlertSubscriptionsManagement;